import React, { useEffect, useRef, useState } from 'react'
import LoadingIcon from '../../../../../../components/LoadingIcon'
import HoverTooltip from '../../../../../../components/HoverTooltip'

import { CurrencyList } from '../../../types'
import { Purchase, PurchaseLot } from '../../../../Purchases/types'
import { PurchaseMethod } from '../../../../Org/PurchaseMethods/types'

const PAGE_SIZE = 50

type SelectedPurchaseResult = {
    purchase: Purchase
    purchase_lot: PurchaseLot
    purchase_method: PurchaseMethod
}

type Props = {
    open: boolean
    theme: string
    year: number | null
    currency_data: CurrencyList[]
    onClose: () => void
    onSelect: (item: SelectedPurchaseResult) => void
}

const toNumberOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null
    const n = Number(String(value).replace(',', '.'))
    return Number.isNaN(n) ? null : n
}

const getCurrencySign = (
    currencyData: CurrencyList[],
    digitalCode: string | number | null | undefined
): string => {
    if (!digitalCode) return ''

    if (Number(digitalCode) === 643) return ''

    return (
        currencyData.find((c) => c.num_code === String(digitalCode))
            ?.char_sign || '?'
    )
}

const formatDate = (value: string | null | undefined): string => {
    if (!value) return '-'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'

    return date.toLocaleDateString('ru-RU')
}

const formatMoney = (value: unknown): string => {
    const n = toNumberOrNull(value)
    if (n === null) return '-'

    return n.toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
}

const getLotPrice = (lot: PurchaseLot): number | string | null => {
    if (lot.initial_sum !== null && lot.initial_sum !== undefined) {
        return lot.initial_sum
    }

    if (Number(lot.currency?.digital_code) === 643) {
        return lot.max_contract_price ?? null
    }

    return lot.max_contract_price_rub ?? lot.max_contract_price ?? null
}

const ContractPurchaseSelectPopup: React.FC<Props> = (props) => {
    const [items, setItems] = useState<Purchase[]>([])
    const [lots, setLots] = useState<PurchaseLot[]>([])

    const [selectedPurchaseGuid, setSelectedPurchaseGuid] = useState<
        string | null
    >(null)
    const [selectedLotGuid, setSelectedLotGuid] = useState<string | null>(null)

    const [searchValue, setSearchValue] = useState('')
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    const [loading, setLoading] = useState(false)
    const [pageLoading, setPageLoading] = useState(false)
    const [searchLoading, setSearchLoading] = useState(false)
    const [lotsLoading, setLotsLoading] = useState(false)

    const scrollRef = useRef<HTMLDivElement | null>(null)
    const isFirstSearchEffect = useRef(true)

    const selectedPurchase =
        items.find((item) => item.guid === selectedPurchaseGuid) ?? null

    const loadPurchases = async (
        pageValue: number,
        textQuery: string,
        append: boolean
    ): Promise<void> => {
        if (!props.year) return

        const params = new URLSearchParams({
            year: String(props.year),
            page: String(pageValue),
            size: String(PAGE_SIZE),
        })

        if (textQuery.trim()) {
            params.append('text_query', textQuery.trim())
        }

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/purchases_by_year_and_text_query?${params.toString()}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка загрузки извещений о закупке')
        }

        const data = await response.json()
        const loadedItems = Array.isArray(data) ? data : []

        setItems((prev) => (append ? [...prev, ...loadedItems] : loadedItems))
        setHasMore(loadedItems.length === PAGE_SIZE)
        setPage(pageValue)
    }

    const loadLots = async (purchaseGuid: string): Promise<void> => {
        setLotsLoading(true)

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/purchases/lots?purchase_guid=${purchaseGuid}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (!response.ok) {
                throw new Error('Ошибка загрузки лотов извещения')
            }

            const data = await response.json()
            setLots(Array.isArray(data) ? data : [])
        } catch (e) {
            console.error(e)
            setLots([])
        } finally {
            setLotsLoading(false)
        }
    }

    const loadPurchaseMethod = async (
        purchaseMethodCode: number | null
    ): Promise<PurchaseMethod | null> => {
        if (!purchaseMethodCode) return null

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/purchase_method_active/purchase_method_code?purchase_method_code=${purchaseMethodCode}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка загрузки способа закупки')
        }

        return await response.json()
    }

    const handleInitialLoad = async () => {
        if (!props.year) return

        setLoading(true)
        setSelectedPurchaseGuid(null)
        setSelectedLotGuid(null)
        setLots([])
        setItems([])
        setPage(1)
        setHasMore(true)

        try {
            await loadPurchases(1, '', false)
        } catch (e) {
            console.error(e)
            setItems([])
            setHasMore(false)
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = async (text: string) => {
        setSearchLoading(true)
        setSelectedPurchaseGuid(null)
        setSelectedLotGuid(null)
        setLots([])

        try {
            await loadPurchases(1, text, false)
        } catch (e) {
            console.error(e)
            setItems([])
            setHasMore(false)
        } finally {
            setSearchLoading(false)
        }
    }

    const handleScroll = async () => {
        const el = scrollRef.current
        if (!el || pageLoading || loading || searchLoading || !hasMore) return

        const isNearBottom =
            el.scrollTop + el.clientHeight >= el.scrollHeight - 40

        if (!isNearBottom) return

        setPageLoading(true)
        try {
            await loadPurchases(page + 1, searchValue, true)
        } catch (e) {
            console.error(e)
            setHasMore(false)
        } finally {
            setPageLoading(false)
        }
    }

    const handleSelectPurchase = async (purchase: Purchase) => {
        // Повторное нажатие по открытому извещению — закрываем аккордеон
        if (selectedPurchaseGuid === purchase.guid) {
            setSelectedPurchaseGuid(null)
            setSelectedLotGuid(null)

            // lots можно не очищать сразу, чтобы была плавная анимация закрытия
            return
        }

        setSelectedPurchaseGuid(purchase.guid)
        setSelectedLotGuid(null)
        setLots([])

        await loadLots(purchase.guid)
    }

    const handleSelectLot = async (lot: PurchaseLot) => {
        if (!selectedPurchase) return

        try {
            const purchaseMethod = await loadPurchaseMethod(
                selectedPurchase.purchase_method_code
            )

            if (!purchaseMethod) return

            props.onSelect({
                purchase: selectedPurchase,
                purchase_lot: lot,
                purchase_method: purchaseMethod,
            })
        } catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        if (!props.open) return

        isFirstSearchEffect.current = true
        setSearchValue('')
        handleInitialLoad()
    }, [props.open, props.year])

    useEffect(() => {
        if (!props.open) return

        if (isFirstSearchEffect.current) {
            isFirstSearchEffect.current = false
            return
        }

        const timer = setTimeout(() => {
            handleSearch(searchValue)
        }, 700)

        return () => clearTimeout(timer)
    }, [searchValue, props.open])

    if (!props.open) return null

    return (
        <div className="fixed top-0 left-0 z-50 w-full h-full bg-[#00000050] flex justify-center items-center">
            <div
                className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[1100px] h-fit flex flex-col gap-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-col gap-1">
                    <div className="text-[16px] text-[#181818] dark:text-[#FFFFFF]">
                        Выбор извещения о закупке и лота
                    </div>
                    <div className="text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                        Год: {props.year ?? '-'}
                    </div>
                </div>

                {!props.year ? (
                    <div className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                        Не удалось определить год закупки.
                    </div>
                ) : loading ? (
                    <div className="flex justify-center items-center h-[580px]">
                        <LoadingIcon />
                    </div>
                ) : (
                    <>
                        <div className="flex flex-row gap-3 items-end">
                            <div className="flex flex-col flex-1">
                                <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Поиск извещения о закупке
                                </label>
                                <div className="relative flex flex-row w-full items-center justify-end">
                                    <input
                                        type="text"
                                        className="form-input user-input-placeholder w-full"
                                        value={searchValue}
                                        onChange={(e) =>
                                            setSearchValue(e.target.value)
                                        }
                                        placeholder="Введите номер извещения / наименование закупки"
                                        style={{
                                            colorScheme:
                                                props.theme === 'dark'
                                                    ? 'dark'
                                                    : 'light',
                                        }}
                                    />
                                    <div className="absolute flex w-6 h-6 mr-1 rounded-full items-center justify-center">
                                        <svg
                                            className="w-4 h-4"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            id="loupe"
                                        >
                                            <path
                                                d="M21.71,20.29,18,16.61A9,9,0,1,0,16.61,18l3.68,3.68a1,1,0,0,0,1.42,0A1,1,0,0,0,21.71,20.29ZM11,18a7,7,0,1,1,7-7A7,7,0,0,1,11,18Z"
                                                fill={
                                                    props.theme === 'dark'
                                                        ? '#FFFFFF'
                                                        : '#181818'
                                                }
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col w-full h-[500px]">
                            <div className="flex flex-row w-full gap-1 p-1 py-3 px-2 items-center justify-center rounded-t-md text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                <div className="flex w-32 items-center justify-center text-center">
                                    № извещения
                                </div>
                                <div className="flex flex-1 items-center justify-center text-center">
                                    Наименование закупки
                                </div>
                                <div className="flex w-40 items-center justify-center text-center">
                                    Способ закупки
                                </div>
                                <div className="flex w-28 items-center justify-center text-center">
                                    Дата публикации
                                </div>
                                <div className="flex w-24 items-center justify-center text-center">
                                    Лотов
                                </div>
                            </div>

                            <div
                                ref={scrollRef}
                                onScroll={handleScroll}
                                className="flex flex-col w-full h-[500px] gap-1 p-1 items-start justify-start rounded-b-md overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E] text-[11px]"
                            >
                                {searchLoading ? (
                                    <div className="flex justify-center items-center h-[500px] w-full">
                                        <LoadingIcon />
                                    </div>
                                ) : items.length ? (
                                    <>
                                        {/* {items.map((item) => (
                                            <button
                                                key={item.guid}
                                                className={`flex flex-row w-full h-fit gap-1 p-1 items-center justify-center rounded-md cursor-pointer text-[#181818] dark:text-[#FFFFFF] ${
                                                    selectedPurchaseGuid ===
                                                    item.guid
                                                        ? 'bg-[#ffffff] dark:bg-[#373538]'
                                                        : 'bg-[#f6f6f6] dark:bg-[#2a282b] hover:bg-[#ffffff] hover:dark:bg-[#373538]'
                                                }`}
                                                onClick={() =>
                                                    handleSelectPurchase(item)
                                                }
                                            >
                                                <div className="flex w-32 p-1 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                    {item.registration_number ??
                                                        '-'}
                                                </div>

                                                <div className="flex flex-1 p-1 items-start justify-start text-left">
                                                    <HoverTooltip
                                                        tooltipText={
                                                            item.name || '-'
                                                        }
                                                    >
                                                        <p className="fourrow">
                                                            {item.name || '-'}
                                                        </p>
                                                    </HoverTooltip>
                                                </div>

                                                <div className="flex w-40 p-1 items-center justify-center text-center">
                                                    <HoverTooltip
                                                        tooltipText={
                                                            item.purchase_method_name ||
                                                            '-'
                                                        }
                                                    >
                                                        <p className="fourrow">
                                                            {item.purchase_method_name ||
                                                                '-'}
                                                        </p>
                                                    </HoverTooltip>
                                                </div>

                                                <div className="flex w-28 p-1 items-center justify-center text-center">
                                                    {formatDate(
                                                        item.publication_date_time
                                                    )}
                                                </div>

                                                <div className="flex w-24 p-1 items-center justify-center text-center">
                                                    {(item as any).lots_count ??
                                                        '-'}
                                                </div>
                                            </button>
                                        ))} */}

                                        {items.map((item) => {
                                            const isSelected =
                                                selectedPurchaseGuid ===
                                                item.guid

                                            return (
                                                <div
                                                    key={item.guid}
                                                    className="flex flex-col w-full"
                                                >
                                                    <button
                                                        className={`flex flex-row w-full h-fit gap-1 p-1 items-center justify-center rounded-md cursor-pointer text-[#181818] dark:text-[#FFFFFF] ${
                                                            isSelected
                                                                ? 'bg-[#ffffff] dark:bg-[#373538]'
                                                                : 'bg-[#f6f6f6] dark:bg-[#2a282b] hover:bg-[#ffffff] hover:dark:bg-[#373538]'
                                                        }`}
                                                        onClick={() =>
                                                            handleSelectPurchase(
                                                                item
                                                            )
                                                        }
                                                    >
                                                        <div className="flex w-32 p-1 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                            {item.registration_number ??
                                                                '-'}
                                                        </div>

                                                        <div className="flex flex-1 p-1 items-start justify-start text-left">
                                                            <HoverTooltip
                                                                tooltipText={
                                                                    item.name ||
                                                                    '-'
                                                                }
                                                            >
                                                                <p className="fourrow">
                                                                    {item.name ||
                                                                        '-'}
                                                                </p>
                                                            </HoverTooltip>
                                                        </div>

                                                        <div className="flex w-40 p-1 items-center justify-center text-center">
                                                            <HoverTooltip
                                                                tooltipText={
                                                                    item.purchase_method_name ||
                                                                    '-'
                                                                }
                                                            >
                                                                <p className="fourrow">
                                                                    {item.purchase_method_name ||
                                                                        '-'}
                                                                </p>
                                                            </HoverTooltip>
                                                        </div>

                                                        <div className="flex w-28 p-1 items-center justify-center text-center">
                                                            {formatDate(
                                                                item.publication_date_time
                                                            )}
                                                        </div>

                                                        <div className="flex w-24 p-1 items-center justify-center text-center">
                                                            {(item as any)
                                                                .lots_count ??
                                                                '-'}
                                                        </div>
                                                    </button>

                                                    <div
                                                        className={`w-[calc(100%_-_60px)] ml-[60px] overflow-hidden transition-all duration-300 ease-in-out ${
                                                            isSelected
                                                                ? 'max-h-[420px] opacity-100 translate-y-0 mt-1 mb-1'
                                                                : 'max-h-0 opacity-0 -translate-y-2 mt-0 mb-0'
                                                        }`}
                                                    >
                                                        <div
                                                            className={`flex flex-col w-full rounded-md overflow-hidden transition-all duration-300 ease-in-out ${
                                                                isSelected
                                                                    ? 'scale-y-100'
                                                                    : 'scale-y-95'
                                                            }`}
                                                        >
                                                            <div className="flex flex-row w-full gap-1 p-1 py-3 px-2 items-center justify-center text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                                                <div className="flex w-16 items-center justify-center text-center">
                                                                    № лота
                                                                </div>
                                                                <div className="flex flex-1 items-center justify-center text-center">
                                                                    Предмет лота
                                                                </div>
                                                                <div className="flex w-36 items-center justify-center text-center">
                                                                    Цена
                                                                </div>
                                                                <div className="flex w-28 items-center justify-center text-center">
                                                                    Валюта
                                                                </div>
                                                                <div className="flex w-40 items-center justify-center text-center">
                                                                    Позиция
                                                                    плана
                                                                </div>
                                                            </div>

                                                            <div className="flex flex-col w-full max-h-[350px] gap-1 p-1 overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E]">
                                                                {lotsLoading &&
                                                                isSelected ? (
                                                                    <div className="flex justify-center items-center h-[70px] w-full">
                                                                        <LoadingIcon />
                                                                    </div>
                                                                ) : lots.length &&
                                                                  isSelected ? (
                                                                    lots.map(
                                                                        (
                                                                            lot
                                                                        ) => (
                                                                            <button
                                                                                key={
                                                                                    lot.guid
                                                                                }
                                                                                className={`flex flex-row w-full h-fit gap-1 p-1 items-center justify-center rounded-md cursor-pointer text-[#181818] dark:text-[#FFFFFF] ${
                                                                                    selectedLotGuid ===
                                                                                    lot.guid
                                                                                        ? 'bg-[#ffffff] dark:bg-[#373538]'
                                                                                        : 'bg-[#f6f6f6] dark:bg-[#2a282b] hover:bg-[#ffffff] hover:dark:bg-[#373538]'
                                                                                }`}
                                                                                onClick={() =>
                                                                                    setSelectedLotGuid(
                                                                                        lot.guid
                                                                                    )
                                                                                }
                                                                                onDoubleClick={() =>
                                                                                    handleSelectLot(
                                                                                        lot
                                                                                    )
                                                                                }
                                                                            >
                                                                                <div className="flex w-16 p-1 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                                                    {lot.ordinal_number ??
                                                                                        '-'}
                                                                                </div>

                                                                                <div className="flex flex-1 p-1 items-start justify-start text-left">
                                                                                    <HoverTooltip
                                                                                        tooltipText={
                                                                                            lot.subject ||
                                                                                            '-'
                                                                                        }
                                                                                    >
                                                                                        <p className="fourrow">
                                                                                            {lot.subject ||
                                                                                                '-'}
                                                                                        </p>
                                                                                    </HoverTooltip>
                                                                                </div>

                                                                                <div className="flex w-36 p-1 items-center justify-end text-right">
                                                                                    {getCurrencySign(
                                                                                        props.currency_data,
                                                                                        lot
                                                                                            .currency
                                                                                            ?.digital_code
                                                                                    )}{' '}
                                                                                    {formatMoney(
                                                                                        getLotPrice(
                                                                                            lot
                                                                                        )
                                                                                    )}
                                                                                </div>

                                                                                <div className="flex w-28 p-1 items-center justify-center text-center">
                                                                                    {lot
                                                                                        .currency
                                                                                        ?.code ??
                                                                                        '-'}
                                                                                </div>

                                                                                <div className="flex w-40 p-1 items-center justify-center text-center">
                                                                                    {lot.plan_registration_number ? (
                                                                                        <div>
                                                                                            План
                                                                                            №{' '}
                                                                                            {
                                                                                                lot.plan_registration_number
                                                                                            }
                                                                                            <br />
                                                                                            позиция
                                                                                            №{' '}
                                                                                            {lot.plan_pos_number ??
                                                                                                '-'}
                                                                                        </div>
                                                                                    ) : (
                                                                                        '-'
                                                                                    )}
                                                                                </div>
                                                                            </button>
                                                                        )
                                                                    )
                                                                ) : isSelected ? (
                                                                    <div className="flex w-full h-[50px] items-center justify-center text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                                        Лоты не
                                                                        найдены
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}

                                        {pageLoading && (
                                            <div className="flex justify-center items-center h-[40px] w-full">
                                                <LoadingIcon />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex w-full h-[50px] items-center justify-center text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Извещения не найдены
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* <div className="flex flex-col w-full h-[250px]">
                            <div className="flex flex-row w-full gap-1 p-1 py-3 px-2 items-center justify-center rounded-t-md text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                <div className="flex w-16 items-center justify-center text-center">
                                    № лота
                                </div>
                                <div className="flex flex-1 items-center justify-center text-center">
                                    Предмет лота
                                </div>
                                <div className="flex w-36 items-center justify-center text-center">
                                    Цена
                                </div>
                                <div className="flex w-28 items-center justify-center text-center">
                                    Валюта
                                </div>
                                <div className="flex w-40 items-center justify-center text-center">
                                    Позиция плана
                                </div>
                            </div>

                            <div className="flex flex-col w-full h-[205px] gap-1 p-1 items-start justify-start rounded-b-md overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E] text-[11px]">
                                {!selectedPurchase ? (
                                    <div className="flex w-full h-[50px] items-center justify-center text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Выберите извещение, чтобы посмотреть
                                        лоты
                                    </div>
                                ) : lotsLoading ? (
                                    <div className="flex justify-center items-center h-[190px] w-full">
                                        <LoadingIcon />
                                    </div>
                                ) : lots.length ? (
                                    lots.map((lot) => (
                                        <button
                                            key={lot.guid}
                                            className={`flex flex-row w-full h-fit gap-1 p-1 items-center justify-center rounded-md cursor-pointer text-[#181818] dark:text-[#FFFFFF] ${
                                                selectedLotGuid === lot.guid
                                                    ? 'bg-[#ffffff] dark:bg-[#373538]'
                                                    : 'bg-[#f6f6f6] dark:bg-[#2a282b] hover:bg-[#ffffff] hover:dark:bg-[#373538]'
                                            }`}
                                            onClick={() =>
                                                setSelectedLotGuid(lot.guid)
                                            }
                                            onDoubleClick={() =>
                                                handleSelectLot(lot)
                                            }
                                        >
                                            <div className="flex w-16 p-1 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                {lot.ordinal_number ?? '-'}
                                            </div>

                                            <div className="flex flex-1 p-1 items-start justify-start text-left">
                                                <HoverTooltip
                                                    tooltipText={
                                                        lot.subject || '-'
                                                    }
                                                >
                                                    <p className="fourrow">
                                                        {lot.subject || '-'}
                                                    </p>
                                                </HoverTooltip>
                                            </div>

                                            <div className="flex w-36 p-1 items-center justify-end text-right">
                                                {getCurrencySign(
                                                    props.currency_data,
                                                    lot.currency?.digital_code
                                                )}{' '}
                                                {formatMoney(getLotPrice(lot))}
                                            </div>

                                            <div className="flex w-28 p-1 items-center justify-center text-center">
                                                {lot.currency?.code ?? '-'}
                                            </div>

                                            <div className="flex w-40 p-1 items-center justify-center text-center">
                                                {lot.plan_registration_number
                                                    ? `План №${lot.plan_registration_number}, позиция №${lot.plan_pos_number ?? '-'}`
                                                    : '-'}
                                            </div>
                                        </button>
                                    ))
                                ) : (
                                    <div className="flex w-full h-[50px] items-center justify-center text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Лоты не найдены
                                    </div>
                                )}
                            </div>
                        </div> */}
                    </>
                )}

                <div className="flex justify-end gap-3">
                    <button
                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        onClick={props.onClose}
                    >
                        Отмена
                    </button>

                    <button
                        className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs ${
                            !selectedPurchase || !selectedLotGuid
                                ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]'
                                : 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                        }`}
                        disabled={!selectedPurchase || !selectedLotGuid}
                        onClick={() => {
                            const lot =
                                lots.find(
                                    (item) => item.guid === selectedLotGuid
                                ) ?? null

                            if (lot) {
                                handleSelectLot(lot)
                            }
                        }}
                    >
                        Выбрать
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ContractPurchaseSelectPopup
